import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNewsArticles } from '../../../hooks/useNewsArticles';
import { newsSources } from '../../../mocks/localNews';
import TrendingTicker from './TrendingTicker';

const getSource = (id: string) => newsSources.find((s) => s.id === id);

export default function LocalNewsSection() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'City Council', 'Schools', 'Business', 'Community'];

  const { articles, loading, lastUpdated, refresh } = useNewsArticles();

  const filtered = activeCategory === 'All'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  const breaking = filtered.find((a) => a.isBreaking);
  const featured = filtered.find((a) => a.isFeatured && !a.isBreaking) || filtered[1];
  const rest = filtered.filter((a) => a.id !== breaking?.id && a.id !== featured?.id).slice(0, 4);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-50 rounded-full mb-4">
              <i className="ri-rss-line text-rose-600 text-sm"></i>
              <span className="text-rose-700 text-sm font-semibold tracking-wide uppercase">Live Feed</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
              Local <span className="text-teal-600">News</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-xl">
              Real-time updates from trusted Roseville news sources, curated for our community
            </p>
          </div>

          {/* Source badges + refresh info */}
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {newsSources.slice(0, 4).map((src) => (
                <span
                  key={src.id}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${src.color}`}
                >
                  <i className={`${src.icon} text-sm`}></i>
                  {src.name.split(' ').slice(-1)[0]}
                </span>
              ))}
              <span className="text-xs text-gray-400 font-medium">+{newsSources.length - 4} more</span>
            </div>
            <div className="flex items-center gap-2">
              {lastUpdated && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
                  Updated {formatTime(lastUpdated)}
                </span>
              )}
              <button
                onClick={refresh}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-xs text-gray-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Trending Ticker */}
        <TrendingTicker articles={articles} />

        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {loading && articles.length === 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-6">
              <div className="rounded-2xl h-[380px] bg-gray-200 animate-pulse"></div>
              <div className="rounded-xl h-56 bg-gray-200 animate-pulse"></div>
            </div>
            <div className="lg:col-span-4 space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="rounded-xl h-28 bg-gray-200 animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column — Breaking + Featured */}
              <div className="lg:col-span-8 space-y-6">
                {/* Breaking News */}
                {breaking && (
                  <Link
                    to={`/news/${breaking.id}`}
                    className="block group cursor-pointer"
                  >
                    <div className="relative rounded-2xl overflow-hidden h-[380px] shadow-lg">
                      <img
                        src={breaking.image}
                        alt={breaking.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded-full animate-pulse">
                          <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                          BREAKING
                        </span>
                        {(() => {
                          const src = getSource(breaking.source);
                          return src ? (
                            <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${src.color}`}>
                              {src.name}
                            </span>
                          ) : null;
                        })()}
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full mb-3">
                          {breaking.category}
                        </span>
                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-teal-300 transition-colors leading-tight">
                          {breaking.title}
                        </h3>
                        <p className="text-white/80 text-sm max-w-2xl line-clamp-2">
                          {breaking.excerpt}
                        </p>
                        <div className="flex items-center gap-3 mt-3">
                          <span className="inline-flex items-center gap-1 text-white/60 text-xs">
                            <i className="ri-time-line"></i>
                            {breaking.timeAgo}
                          </span>
                          <span className="inline-flex items-center gap-1 text-teal-300 text-xs font-semibold">
                            Read Full Story <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                )}

                {/* Featured Story */}
                {featured && (
                  <Link
                    to={`/news/${featured.id}`}
                    className="block group cursor-pointer"
                  >
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-gray-100 flex flex-col md:flex-row">
                      <div className="relative w-full md:w-[320px] h-56 md:h-auto flex-shrink-0 overflow-hidden">
                        <img
                          src={featured.image}
                          alt={featured.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-3">
                          {(() => {
                            const src = getSource(featured.source);
                            return src ? (
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium ${src.color}`}>
                                <i className={`${src.icon}`}></i>
                                {src.name}
                              </span>
                            ) : null;
                          })()}
                          <span className="text-xs text-gray-400">{featured.timeAgo}</span>
                        </div>
                        <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-2">{featured.category}</span>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors leading-snug">
                          {featured.title}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                          {featured.excerpt}
                        </p>
                        <span className="inline-flex items-center gap-1 text-teal-600 text-sm font-semibold mt-4 group-hover:gap-2 transition-all">
                          Read Full Story <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                )}
              </div>

              {/* Right Column — Latest Stories */}
              <div className="lg:col-span-4 space-y-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-gray-900 text-lg">Latest Stories</h3>
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                </div>

                {rest.map((article, idx) => {
                  const src = getSource(article.source);
                  return (
                    <Link
                      key={article.id}
                      to={`/news/${article.id}`}
                      className="block group cursor-pointer bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all border border-gray-100"
                    >
                      <div className="flex gap-4">
                        <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top"
                          />
                          <div className="absolute top-1 left-1">
                            <span className="w-5 h-5 rounded-full bg-white/90 flex items-center justify-center text-[10px] font-bold text-gray-600">
                              {idx + 1}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            {src && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${src.color}`}>
                                <i className={src.icon}></i>
                              </span>
                            )}
                            <span className="text-[11px] text-gray-400">{article.timeAgo}</span>
                          </div>
                          <h4 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-teal-600 transition-colors leading-snug">
                            {article.title}
                          </h4>
                          <span className="text-[11px] text-gray-400 mt-1 block">{article.category}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}

                {/* View All CTA */}
                <Link
                  to="/news"
                  className="flex items-center justify-center gap-2 w-full py-3.5 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap mt-2"
                >
                  <i className="ri-newspaper-line"></i>
                  View All Local News
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </>
        )}

        {/* RSS Attribution */}
        <div className="mt-8 flex items-center justify-center gap-3 text-xs text-gray-400">
          <i className="ri-rss-line"></i>
          <span>Aggregated from {newsSources.length} trusted local sources</span>
          <span>&bull;</span>
          <span>Updated every 15 minutes</span>
        </div>
      </div>
    </section>
  );
}
